import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { messages } from '@nesc/shared';
import { ApiError, errorMessage } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AuthShell } from './auth-shell';

export const LoginPage = () => {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (err) {
      // ロック中は待機時間を案内する
      setError(
        err instanceof ApiError && err.code === 'RATE_LIMITED'
          ? messages.auth.locked
          : errorMessage(err),
      );
    } finally {
      setIsPending(false);
    }
  };

  return (
    <AuthShell title={messages.auth.loginTitle}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <p
            role="alert"
            className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold"
          >
            {error}
          </p>
        )}

        <Field label={messages.auth.email} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>

        <Field label={messages.auth.password} required>
          {(props) => (
            <div className="relative">
              <Input
                {...props}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-control p-2 text-foreground-muted hover:bg-muted"
                aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示する'}
              >
                {showPassword ? <EyeOff aria-hidden className="size-5" /> : <Eye aria-hidden className="size-5" />}
              </button>
            </div>
          )}
        </Field>

        <Button type="submit" size="lg" className="w-full" disabled={isPending}>
          {isPending ? messages.common.loading : messages.auth.submit}
        </Button>

        <div className="space-y-2 text-center">
          <Button variant="link" size="sm" type="button" onClick={() => navigate('/password-reset')}>
            {messages.auth.forgot}
          </Button>
          {/* 招待制のため新規登録リンクは置かない */}
          <p className="text-note text-foreground-subtle">{messages.auth.invitedOnly}</p>
        </div>
      </form>
    </AuthShell>
  );
};
